/**
 * Mirrors the Reports.Internal `userCommunicationHistory` contract (US-3250).
 * Backend DTOs: Reports.Core/Models/UserTimelineReport.cs.
 */

export type TimelineEntryKind = 'Communication' | 'Activity';

export interface UserCommunicationTimelineEntry {
    /** UTC ISO timestamp for both streams. */
    occurredAt: string;
    kind: TimelineEntryKind;
    /** Communication channel (Email/Sms). Absent for activity entries. */
    channel?: string;
    /** Communication event type OR activity event type, as a string. */
    eventType: string;
    campaignName?: string;
    templateId?: string;
    clickedUrl?: string;
}

export interface UserTimelineReport {
    userId: string;
    /** Operator-facing display name, or "Deleted user" for an erased user. */
    displayIdentity: string;
    /** True when no live User resolved (de-identified/erased per US-3248). */
    isDeletedUser: boolean;
    timeline: UserCommunicationTimelineEntry[];
}

/**
 * Stable identifier-lookup error codes shared with the backend
 * (Payments.Core UserLookupErrorCodes). The UI switches on these codes,
 * never on message text (US-3385).
 */
export const USER_LOOKUP_ERROR_CODE = {
    MALFORMED: 'malformed_identifier',
    NOT_FOUND: 'user_not_found',
    AMBIGUOUS: 'ambiguous_identifier'
} as const;

export type UserLookupErrorCode =
    (typeof USER_LOOKUP_ERROR_CODE)[keyof typeof USER_LOOKUP_ERROR_CODE];

/**
 * A disambiguation candidate returned with an `ambiguous_identifier` response.
 * Name + id only — the backend never echoes the entered email or any PII.
 */
export interface UserLookupCandidate {
    userId: string;
    displayName: string;
}

/** Machine-stable error envelope for the identifier lookup (non-200 responses). */
export interface UserLookupErrorResponse {
    code: UserLookupErrorCode;
    /** Present only for `ambiguous_identifier`. */
    candidates?: UserLookupCandidate[];
}
